import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Users, UserPlus, Search, Shield, Star, ChevronRight, ChevronDown,
  Copy, Link2, Trash2, Mail, Phone, Briefcase, Globe, Building2,
  CheckCircle2, Clock, AlertTriangle, Plus, X, Send, Eye, Settings
} from 'lucide-react';

const WORKFORCE_TREE = [
  { key: 'internal', label: 'Internal Team', icon: Building2, children: [
    { key: 'executive', label: 'Executive / Strategic' },
    { key: 'creative_leadership', label: 'Creative Leadership' },
    { key: 'production_mgmt', label: 'Production Management' },
    { key: 'pre_production', label: 'Pre-Production' },
    { key: 'production', label: 'Production (Core Pipeline)' },
    { key: 'vfx_simulation', label: 'VFX & Simulation' },
    { key: 'post_production', label: 'Post-Production' },
    { key: 'sound_music', label: 'Sound & Music' },
    { key: 'tech_pipeline', label: 'Tech & Pipeline' },
    { key: 'business', label: 'Business & Distribution' },
    { key: 'support', label: 'Support Functions' },
  ]},
  { key: 'freelancers', label: 'Freelancers', icon: Briefcase, children: [
    { key: 'external_freelancer', label: 'Individual Artists', filter: 'freelancer' },
  ]},
  { key: 'vendors', label: 'Vendors (Studios)', icon: Building2, children: [
    { key: 'external_vendor_producer', label: 'Vendor Producers', filter: 'vendor_producer' },
    { key: 'external_vendor_artist', label: 'Vendor Team', filter: 'vendor_artist' },
  ]},
  { key: 'consultants', label: 'Consultants', icon: Globe, children: [
    { key: 'external_consultant', label: 'Consultants', filter: 'consultant' },
  ]},
  { key: 'clients', label: 'Clients', icon: Users, children: [
    { key: 'external_client', label: 'Client Team', filter: 'client_producer' },
  ]},
];

export default function UsersPage() {
  const { api, user: currentUser } = useAuth();
  const [tab, setTab] = useState('team');
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState({});
  const [departments, setDepartments] = useState({});
  const [invitations, setInvitations] = useState([]);
  const [search, setSearch] = useState('');
  const [filterDept, setFilterDept] = useState('all');
  const [expandedTree, setExpandedTree] = useState({ internal: true });
  const [showInvite, setShowInvite] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [inviteForm, setInviteForm] = useState({ role: 'animator', department: 'production', skill_level: 'mid', max_uses: 1, notes: '' });
  const [addForm, setAddForm] = useState({ email: '', full_name: '', password: '', role: 'animator', department: 'production' });
  const [copiedLink, setCopiedLink] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/users').catch(() => ({ data: [] })),
      api.get('/auth/roles'),
      api.get('/auth/departments'),
      api.get('/invitations').catch(() => ({ data: [] })),
    ]).then(([u, r, d, i]) => {
      setUsers(u.data);
      setRoles(r.data);
      setDepartments(d.data);
      setInvitations(i.data);
    });
  }, [api]);

  const createInvitation = async () => {
    const res = await api.post('/invitations/create', inviteForm);
    setInvitations([res.data.invitation, ...invitations]);
    setCopiedLink(res.data.registration_link);
    navigator.clipboard.writeText(res.data.registration_link);
    setShowInvite(false);
  };

  const addUser = async () => {
    await api.post('/auth/register', addForm);
    const u = await api.get('/users');
    setUsers(u.data);
    setShowAddUser(false);
    setAddForm({ email: '', full_name: '', password: '', role: 'animator', department: 'production' });
  };

  const updateRole = async (userId, newRole) => {
    const roleData = roles[newRole] || {};
    await api.put(`/users/${userId}`, { role: newRole });
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole, department: roleData.department } : u));
  };

  const toggleActive = async (userId, isActive) => {
    await api.put(`/users/${userId}`, { is_active: !isActive });
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_active: !isActive } : u));
  };

  const deleteInvitation = async (id) => {
    await api.delete(`/invitations/${id}`);
    setInvitations(prev => prev.filter(i => i.id !== id));
  };

  const copyInviteLink = (code) => {
    const link = `${window.location.origin}/register/${code}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(code);
    setTimeout(() => setCopiedLink(''), 3000);
  };

  // Group users by department
  const usersByDept = {};
  users.forEach(u => {
    const dept = u.department || roles[u.role]?.department || 'external';
    if (!usersByDept[dept]) usersByDept[dept] = [];
    usersByDept[dept].push(u);
  });

  const filteredUsers = users.filter(u => {
    if (filterDept !== 'all') {
      const dept = u.department || roles[u.role]?.department || '';
      if (dept !== filterDept) return false;
    }
    if (search && !u.full_name?.toLowerCase().includes(search.toLowerCase()) && !u.email?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const roleOptions = Object.entries(roles).sort((a, b) => {
    const deptOrder = Object.keys(departments);
    const dA = deptOrder.indexOf(a[1].department || '');
    const dB = deptOrder.indexOf(b[1].department || '');
    if (dA !== dB) return dA - dB;
    return a[1].label.localeCompare(b[1].label);
  });

  return (
    <div className="space-y-4" data-testid="users-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">HR Command Center</h1>
          <p className="text-xs text-zinc-500 mt-1">{users.length} team members | {Object.keys(roles).length} roles | {invitations.filter(i => i.is_active).length} active invitations</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="invite-btn" onClick={() => setShowInvite(true)} className="h-8 px-4 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-medium rounded-sm flex items-center gap-2">
            <Send className="w-3.5 h-3.5" /> Send Invitation Link
          </button>
          <button data-testid="add-user-btn" onClick={() => setShowAddUser(true)} className="h-8 px-4 bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium rounded-sm flex items-center gap-2">
            <UserPlus className="w-3.5 h-3.5" /> Add User
          </button>
        </div>
      </div>

      {/* Copied Link Banner */}
      {copiedLink && (
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-sm px-4 py-2 flex items-center justify-between animate-fade-in">
          <p className="text-xs text-emerald-400 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Registration link copied to clipboard!</p>
          <button onClick={() => setCopiedLink('')} className="text-zinc-600 hover:text-white"><X className="w-3 h-3" /></button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-zinc-800">
        {[
          { k: 'team', l: 'Workforce Tree', icon: Building2 },
          { k: 'members', l: `All Members (${users.length})`, icon: Users },
          { k: 'invitations', l: `Invitations (${invitations.length})`, icon: Send },
          { k: 'roles', l: `Roles (${Object.keys(roles).length})`, icon: Shield },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
            className={`px-4 py-2 text-xs font-medium border-b-2 -mb-px flex items-center gap-1.5 transition-colors ${tab === t.k ? 'border-blue-500 text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.l}
          </button>
        ))}
      </div>

      {/* TAB: Workforce Tree */}
      {tab === 'team' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
            <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider mb-3">Studio Workforce</h3>
            {WORKFORCE_TREE.map(group => {
              const Icon = group.icon;
              const isExpanded = expandedTree[group.key];
              const groupUsers = group.children.reduce((acc, child) => {
                const dept = child.key.replace('external_', '');
                return acc + (usersByDept[dept]?.length || 0) + (usersByDept[child.filter]?.length || 0);
              }, 0);
              return (
                <div key={group.key} className="mb-1" data-testid={`tree-${group.key}`}>
                  <button onClick={() => setExpandedTree(p => ({ ...p, [group.key]: !p[group.key] }))}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-300 hover:bg-zinc-800/50 rounded-sm transition-colors">
                    {isExpanded ? <ChevronDown className="w-3 h-3 text-blue-400" /> : <ChevronRight className="w-3 h-3 text-zinc-600" />}
                    <Icon className="w-3.5 h-3.5 text-blue-400" />
                    <span className="font-medium">{group.label}</span>
                    <span className="ml-auto text-[10px] font-mono text-zinc-600">{groupUsers}</span>
                  </button>
                  {isExpanded && (
                    <div className="ml-6 border-l border-zinc-800 pl-3 space-y-0.5">
                      {group.children.map(child => {
                        const deptKey = child.filter || child.key;
                        const count = (usersByDept[deptKey]?.length || 0) + (usersByDept[child.key]?.length || 0);
                        return (
                          <button key={child.key} onClick={() => { setFilterDept(deptKey); setTab('members'); }}
                            className="w-full flex items-center gap-2 px-2 py-1.5 text-[11px] text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800/30 rounded-sm transition-colors">
                            <span className="flex-1 text-left">{child.label}</span>
                            <span className="text-[9px] font-mono text-zinc-700">{count}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Quick Stats */}
          <div className="lg:col-span-2 space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
                <Users className="w-4 h-4 text-blue-400 mb-2" />
                <p className="text-xl font-heading font-bold text-white">{users.length}</p>
                <p className="text-[10px] text-zinc-500">Total Members</p>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 mb-2" />
                <p className="text-xl font-heading font-bold text-emerald-400">{users.filter(u => u.is_active).length}</p>
                <p className="text-[10px] text-zinc-500">Active</p>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
                <Send className="w-4 h-4 text-amber-400 mb-2" />
                <p className="text-xl font-heading font-bold text-amber-400">{invitations.filter(i => i.is_active).length}</p>
                <p className="text-[10px] text-zinc-500">Pending Invites</p>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
                <Shield className="w-4 h-4 text-violet-400 mb-2" />
                <p className="text-xl font-heading font-bold text-violet-400">{Object.keys(departments).length}</p>
                <p className="text-[10px] text-zinc-500">Departments</p>
              </div>
            </div>

            {/* Department Breakdown */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
              <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider mb-3">Department Breakdown</h3>
              <div className="space-y-2">
                {Object.entries(departments).map(([key, dept]) => {
                  const count = usersByDept[key]?.length || 0;
                  return (
                    <div key={key} className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: dept.color }} />
                      <span className="text-xs text-zinc-400 flex-1">{dept.label}</span>
                      <span className="text-xs font-mono text-zinc-500">{count}</span>
                      <div className="w-24 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${users.length ? (count / users.length * 100) : 0}%`, backgroundColor: dept.color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB: All Members */}
      {tab === 'members' && (
        <>
          <div className="flex gap-3 items-center">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
              <input data-testid="user-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email..." className="w-full h-8 pl-9 pr-3 bg-zinc-900 border border-zinc-800 rounded-sm text-xs text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
            <select data-testid="dept-filter" value={filterDept} onChange={e => setFilterDept(e.target.value)} className="h-8 bg-zinc-900 border border-zinc-800 rounded-sm text-xs text-zinc-300 px-3 focus:outline-none">
              <option value="all">All Departments</option>
              {Object.entries(departments).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
            <table className="w-full" data-testid="users-table">
              <thead>
                <tr className="border-b border-zinc-800 text-[10px] text-zinc-500 uppercase tracking-wider">
                  <th className="text-left px-4 py-2.5 font-medium">Member</th>
                  <th className="text-left px-4 py-2.5 font-medium">Role</th>
                  <th className="text-left px-4 py-2.5 font-medium">Type / Dept</th>
                  <th className="text-left px-4 py-2.5 font-medium">NDA</th>
                  <th className="text-left px-4 py-2.5 font-medium">Status</th>
                  <th className="text-left px-4 py-2.5 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/30">
                {filteredUsers.map(u => {
                  const roleData = roles[u.role] || {};
                  return (
                    <tr key={u.id} className="hover:bg-zinc-800/20 transition-colors" data-testid={`user-row-${u.id}`}>
                      <td className="px-4 py-2.5">
                        <p className="text-xs text-zinc-200">{u.full_name}</p>
                        <p className="text-[10px] font-mono text-zinc-600">{u.email}</p>
                        {u.phone && <p className="text-[9px] text-zinc-700">{u.phone}</p>}
                      </td>
                      <td className="px-4 py-2.5">
                        <select value={u.role} onChange={e => updateRole(u.id, e.target.value)}
                          className="h-6 bg-zinc-950 border border-zinc-800 rounded text-[10px] text-zinc-300 px-1.5 focus:outline-none max-w-[160px]">
                          {roleOptions.map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex flex-col gap-1">
                          <span className={`inline-block text-[9px] px-2 py-0.5 rounded-sm font-medium w-fit ${
                            u.workforce_type === 'employee' ? 'bg-blue-500/15 text-blue-400 border border-blue-500/20' :
                            u.workforce_type === 'freelancer' ? 'bg-amber-500/15 text-amber-400 border border-amber-500/20' :
                            u.workforce_type === 'vendor' ? 'bg-violet-500/15 text-violet-400 border border-violet-500/20' :
                            u.workforce_type === 'client' ? 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/20' :
                            'bg-zinc-800 text-zinc-400'
                          }`}>
                            {u.workforce_type === 'employee' ? 'EMPLOYEE' :
                             u.workforce_type === 'freelancer' ? 'FREELANCER' :
                             u.workforce_type === 'vendor' ? 'VENDOR' :
                             u.workforce_type === 'client' ? 'CLIENT' :
                             (u.role?.includes('vendor') ? 'VENDOR' : u.role?.includes('client') ? 'CLIENT' : u.role === 'freelancer' ? 'FREELANCER' : 'EMPLOYEE')}
                          </span>
                          <span className="text-[9px] text-zinc-600">{departments[roleData.department]?.label || roleData.department || '-'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`status-${u.nda_status === 'signed' ? 'completed' : 'pending'} px-1.5 py-0.5 rounded-full text-[9px] font-medium`}>{u.nda_status}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-medium ${u.is_active ? 'status-completed' : 'status-blocked'}`}>{u.is_active ? 'Active' : 'Inactive'}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex gap-1 flex-wrap">
                          <button onClick={() => window.location.href = `/profile/${u.id}`}
                            className="h-6 px-2 bg-blue-500/20 text-blue-400 text-[10px] rounded-sm hover:bg-blue-500/30 flex items-center gap-1">
                            <Eye className="w-3 h-3" /> Profile
                          </button>
                          <button onClick={() => toggleActive(u.id, u.is_active)}
                            className={`h-6 px-2 text-[10px] rounded-sm ${u.is_active ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' : 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'}`}>
                            {u.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filteredUsers.length === 0 && <p className="text-xs text-zinc-600 text-center py-8">No members found</p>}
          </div>
        </>
      )}

      {/* TAB: Invitations */}
      {tab === 'invitations' && (
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <p className="text-xs text-zinc-500">{invitations.length} total invitations</p>
            <button onClick={() => setShowInvite(true)} className="h-7 px-3 bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-medium rounded-sm flex items-center gap-1.5">
              <Plus className="w-3 h-3" /> New Invitation
            </button>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
            <div className="divide-y divide-zinc-800/30">
              {invitations.map(inv => {
                const roleData = roles[inv.role] || {};
                const link = `${window.location.origin}/register/${inv.code}`;
                return (
                  <div key={inv.id} className="px-4 py-3 flex items-center justify-between" data-testid={`invite-${inv.id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-zinc-200">{roleData.label || inv.role}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-sm" style={{ backgroundColor: `${roleData.color || '#71717a'}15`, color: roleData.color }}>
                          {inv.department}
                        </span>
                        <span className="text-[9px] text-zinc-600">{inv.skill_level}</span>
                        {!inv.is_active && <span className="text-[9px] text-red-400">Deactivated</span>}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[9px] font-mono text-zinc-600 truncate max-w-xs">{link}</span>
                        <span className="text-[9px] text-zinc-700">Used: {inv.uses}/{inv.max_uses}</span>
                      </div>
                      {inv.notes && <p className="text-[9px] text-zinc-700 mt-0.5">{inv.notes}</p>}
                    </div>
                    <div className="flex gap-1.5 ml-3">
                      <button onClick={() => copyInviteLink(inv.code)}
                        className={`h-6 px-2 text-[9px] rounded-sm flex items-center gap-1 ${copiedLink === inv.code ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'}`}>
                        <Copy className="w-3 h-3" /> {copiedLink === inv.code ? 'Copied!' : 'Copy Link'}
                      </button>
                      <button onClick={() => deleteInvitation(inv.id)} className="h-6 px-2 bg-red-500/10 text-red-400 text-[9px] rounded-sm hover:bg-red-500/20">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                );
              })}
              {invitations.length === 0 && <p className="text-xs text-zinc-600 text-center py-8">No invitations created yet</p>}
            </div>
          </div>
        </div>
      )}

      {/* TAB: Roles */}
      {tab === 'roles' && (
        <div className="space-y-3">
          {Object.entries(departments).map(([deptKey, dept]) => {
            const deptRoles = roleOptions.filter(([_, r]) => r.department === deptKey);
            if (deptRoles.length === 0) return null;
            return (
              <div key={deptKey} className="bg-zinc-900 border border-zinc-800 rounded-sm">
                <div className="px-4 py-2.5 border-b border-zinc-800 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: dept.color }} />
                  <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">{dept.label}</h3>
                  <span className="text-[10px] font-mono text-zinc-600 ml-auto">{deptRoles.length} roles</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1 p-2">
                  {deptRoles.map(([key, role]) => (
                    <div key={key} className="px-3 py-2 bg-zinc-800/30 rounded-sm hover:bg-zinc-800/50 transition-colors">
                      <p className="text-[11px] text-zinc-200">{role.label}</p>
                      <p className="text-[9px] text-zinc-600">{role.level} | {usersByDept[deptKey]?.filter(u => u.role === key).length || 0} members</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Invitation Modal */}
      {showInvite && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" data-testid="invite-modal">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 w-[28rem]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-heading font-semibold text-white">Create Registration Link</h3>
              <button onClick={() => setShowInvite(false)}><X className="w-4 h-4 text-zinc-600" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] text-zinc-500 uppercase">Department</label>
                <select value={inviteForm.department} onChange={e => setInviteForm({...inviteForm, department: e.target.value})}
                  className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-xs text-zinc-300">
                  {Object.entries(departments).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] text-zinc-500 uppercase">Role</label>
                <select value={inviteForm.role} onChange={e => setInviteForm({...inviteForm, role: e.target.value})}
                  className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-xs text-zinc-300">
                  {roleOptions.filter(([_, r]) => !inviteForm.department || r.department === inviteForm.department).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase">Skill Level</label>
                  <select value={inviteForm.skill_level} onChange={e => setInviteForm({...inviteForm, skill_level: e.target.value})}
                    className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-xs text-zinc-300">
                    {['junior', 'mid', 'senior', 'lead', 'head'].map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase">Max Uses</label>
                  <input type="number" min="1" value={inviteForm.max_uses} onChange={e => setInviteForm({...inviteForm, max_uses: parseInt(e.target.value) || 1})}
                    className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-xs text-white" />
                </div>
              </div>
              <div>
                <label className="text-[10px] text-zinc-500 uppercase">Notes</label>
                <input value={inviteForm.notes} onChange={e => setInviteForm({...inviteForm, notes: e.target.value})}
                  className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-xs text-white" placeholder="e.g. TSM project animator" />
              </div>
            </div>
            <button onClick={createInvitation} className="w-full h-9 mt-4 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-medium rounded-sm flex items-center justify-center gap-2">
              <Send className="w-3.5 h-3.5" /> Generate & Copy Link
            </button>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddUser && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" data-testid="add-user-modal">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 w-[28rem]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-heading font-semibold text-white">Add User Directly</h3>
              <button onClick={() => setShowAddUser(false)}><X className="w-4 h-4 text-zinc-600" /></button>
            </div>
            <div className="space-y-3">
              <div><label className="text-[10px] text-zinc-500 uppercase">Full Name</label>
                <input value={addForm.full_name} onChange={e => setAddForm({...addForm, full_name: e.target.value})} className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-xs text-white" /></div>
              <div><label className="text-[10px] text-zinc-500 uppercase">Email</label>
                <input type="email" value={addForm.email} onChange={e => setAddForm({...addForm, email: e.target.value})} className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-xs text-white" /></div>
              <div><label className="text-[10px] text-zinc-500 uppercase">Password</label>
                <input type="password" value={addForm.password} onChange={e => setAddForm({...addForm, password: e.target.value})} className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-xs text-white" /></div>
              <div><label className="text-[10px] text-zinc-500 uppercase">Role</label>
                <select value={addForm.role} onChange={e => setAddForm({...addForm, role: e.target.value})} className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-xs text-zinc-300">
                  {roleOptions.map(([k, v]) => <option key={k} value={k}>{v.label} ({departments[v.department]?.label})</option>)}
                </select></div>
            </div>
            <button onClick={addUser} disabled={!addForm.email || !addForm.full_name} className="w-full h-9 mt-4 bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium rounded-sm disabled:opacity-40">Add User</button>
          </div>
        </div>
      )}
    </div>
  );
}
